import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { CONCEPTOS_OPTIONS } from "./conceptos";
import {
  MOTIVO_MAX_LENGTH,
  hoyIso,
  idDeEmpresa,
  nombreDeEmpresa,
  usuarioActual,
} from "./utils";

const emptyForm = () => ({
  id_empresa: "",
  concepto: "",
  monto: "",
  motivo: "",
  fecha: hoyIso(),
});

const PagosNoProveedorFormDialog = ({
  open,
  mode = "alta",
  initial = null,
  empresas = [],
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");

  const isEdit = mode === "edicion";

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit && initial) {
      setForm({
        id_empresa:
          initial.id_empresa ??
          initial.idempresa ??
          initial.idEmpresa ??
          "",
        concepto: initial.concepto ?? "",
        monto:
          initial.monto != null && initial.monto !== ""
            ? String(initial.monto)
            : "",
        motivo: initial.motivo ?? "",
        fecha: initial.fecha
          ? String(initial.fecha).slice(0, 10)
          : hoyIso(),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, isEdit, initial]);

  const empresasOptions = useMemo(
    () =>
      (empresas || []).map((emp) => ({
        id: idDeEmpresa(emp),
        label: nombreDeEmpresa(emp),
      })),
    [empresas]
  );

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (form.id_empresa === "" || form.id_empresa == null) {
      return "Seleccioná una empresa";
    }
    if (!form.concepto) return "Seleccioná un concepto";
    const montoNum = Number(form.monto);
    if (!form.monto || Number.isNaN(montoNum) || montoNum <= 0) {
      return "El monto debe ser mayor a 0";
    }
    const motivo = String(form.motivo || "").trim();
    if (!motivo) return "El motivo es obligatorio";
    if (motivo.length > MOTIVO_MAX_LENGTH) {
      return `El motivo no puede superar ${MOTIVO_MAX_LENGTH} caracteres`;
    }
    if (!form.fecha) return "La fecha es obligatoria";
    return "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    const user = usuarioActual();
    onSubmit?.({
      id_empresa: Number(form.id_empresa) || form.id_empresa,
      concepto: form.concepto,
      monto: Number(form.monto),
      motivo: String(form.motivo).trim(),
      fecha: form.fecha,
      user,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit} autoComplete="off">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? "Editar pago" : "Nuevo pago"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="pnp-empresa-label">Empresa</InputLabel>
                <Select
                  labelId="pnp-empresa-label"
                  label="Empresa"
                  value={form.id_empresa}
                  onChange={(e) => setField("id_empresa", e.target.value)}
                  disabled={saving}
                >
                  {empresasOptions.map((opt) => (
                    <MenuItem key={String(opt.id)} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="pnp-concepto-label">Concepto</InputLabel>
                <Select
                  labelId="pnp-concepto-label"
                  label="Concepto"
                  value={form.concepto}
                  onChange={(e) => setField("concepto", e.target.value)}
                  disabled={saving}
                >
                  {CONCEPTOS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Monto"
                name="monto"
                type="number"
                size="small"
                fullWidth
                required
                value={form.monto}
                onChange={(e) => setField("monto", e.target.value)}
                disabled={saving}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha"
                name="fecha"
                type="date"
                size="small"
                fullWidth
                required
                value={form.fecha}
                onChange={(e) => setField("fecha", e.target.value)}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Motivo"
                name="motivo"
                size="small"
                fullWidth
                required
                multiline
                minRows={2}
                value={form.motivo}
                onChange={(e) => setField("motivo", e.target.value)}
                disabled={saving}
                inputProps={{ maxLength: MOTIVO_MAX_LENGTH }}
                helperText={`${String(form.motivo || "").length}/${MOTIVO_MAX_LENGTH}`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PagosNoProveedorFormDialog;
